import Phaser from "phaser";
import { Biome, BIOME_PALETTES } from "../maps/maps";
import { getCoverProjection, MapData, TileKind } from "./MapData";
import { isFoliageArea, type FoliageZone } from "../systems/FoliageOcclusions";

/** Stable hash → grass shade index. Keeps each cell’s color the same across
 *  renders without having to store the assignment on the map. */
function grassShadeIndex(c: number, r: number, n: number): number {
  let h = (c * 374761393 + r * 668265263) >>> 0;
  h = (h ^ (h >>> 13)) * 1274126177 >>> 0;
  h = h ^ (h >>> 16);
  return h % n;
}

const TILE_TEXTURE: Record<TileKind, string | null> = {
  grass:        null,            // alternating grass/grass-alt handled below
  path:         "tile-path",
  rock:         "tile-rock",
  fountain:     "tile-fountain",
  torch:        "tile-torch",
  banner:       "tile-banner",
  pine:         "tile-pine",
  water:        "tile-water",
  ice_shard:    "tile-ice-shard",
  "castle-gate": "tile-castle-gate"
};

export class MapRenderer {
  private foliageSprites: Phaser.GameObjects.Image[] = [];
  private foliageMaskGfx: Phaser.GameObjects.Graphics[] = [];

  /**
   * @param backgroundImage Optional pre-rendered map texture key. When set
   *   AND the texture is loaded, the painted background fully replaces the
   *   procedural tile pass (the painted art already contains grass, path,
   *   castle, decorations). Tile data is preserved untouched so game logic
   *   (movement, build validation) continues to work.
   */
  constructor(
    private scene: Phaser.Scene,
    private map: MapData,
    private biome: Biome = "plains",
    private backgroundImage: string | null = null,
  ) {}

  /** True when the painted background is in use (so callers can hide things
   *  that would only make sense over the procedural look, e.g. the path
   *  guideline overlay). */
  hasBackgroundImage(): boolean {
    return Boolean(this.backgroundImage)
      && this.scene.textures.exists(this.backgroundImage!);
  }

  draw(): void {
    if (this.hasBackgroundImage()) {
      this.drawBackgroundImage();
      this.drawFoliageOcclusions(this.map.foliageOcclusions);
      return;
    }
    this.drawTiles();
  }

  private drawBackgroundImage(): void {
    // Cover-fit instead of stretching the 16:9 artwork into the wider 20:9
    // gameplay strip. The same projection converts painted road points into
    // enemy waypoints in MapData, so visuals and game logic stay aligned.
    const { offsetX, offsetY, cols, rows, tileSize } = this.map;
    const projection = getCoverProjection(this.map);
    const image = this.scene.add
      .image(
        projection.left + projection.renderWidth / 2,
        projection.top + projection.renderHeight / 2,
        this.backgroundImage!
      )
      .setDisplaySize(projection.renderWidth, projection.renderHeight)
      .setDepth(-10);

    // The cover-fit image extends above and below the battlefield. Mask it so
    // it cannot bleed into either HUD bar even if those layouts change later.
    const maskShape = this.scene.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(offsetX, offsetY, cols * tileSize, rows * tileSize);
    image.setMask(maskShape.createGeometryMask());
  }

  /**
   * Re-paints a crop of the same background art above towers and enemies at
   * each zone, y-sorted like a real obstacle (depth = the zone's bottom edge,
   * matching Tower's `y + TILE_SIZE/2` and Enemy's `y` convention). The crop IS
   * the tree, duplicated on top of whatever stands under it — so a tower or
   * enemy there is hidden without needing any extra art.
   */
  drawFoliageOcclusions(zones: FoliageZone[]): void {
    this.clearFoliageOcclusions();
    if (!this.hasBackgroundImage() || zones.length === 0) return;

    const proj = getCoverProjection(this.map);
    for (const zone of zones) {
      const maskShape = this.scene.make.graphics({ x: 0, y: 0 });
      maskShape.fillStyle(0xffffff, 1);

      let bottomY: number;
      if (isFoliageArea(zone)) {
        if (zone.points.length < 3) { maskShape.destroy(); continue; }
        const pts = zone.points.map(([u, v]) => ({
          x: proj.left + u * proj.renderWidth,
          y: proj.top + v * proj.renderHeight,
        }));
        maskShape.fillPoints(pts, true);
        bottomY = Math.max(...pts.map(p => p.y));
      } else {
        const wx = proj.left + zone.x * proj.renderWidth;
        const wy = proj.top + zone.y * proj.renderHeight;
        const wr = zone.r * proj.renderWidth;
        maskShape.fillCircle(wx, wy, wr);
        bottomY = wy + wr;
      }

      const img = this.scene.add.image(
        proj.left + proj.renderWidth / 2,
        proj.top + proj.renderHeight / 2,
        this.backgroundImage!
      ).setDisplaySize(proj.renderWidth, proj.renderHeight);

      img.setMask(maskShape.createGeometryMask());
      img.setDepth(bottomY);

      this.foliageSprites.push(img);
      this.foliageMaskGfx.push(maskShape);
    }
  }

  /** Tears down every foliage occlusion sprite + its mask graphics (no leaks on re-authoring). */
  clearFoliageOcclusions(): void {
    for (const s of this.foliageSprites) s.destroy();
    for (const g of this.foliageMaskGfx) g.destroy();
    this.foliageSprites = [];
    this.foliageMaskGfx = [];
  }

  private drawTiles(): void {
    const { cols, rows, tileSize, tiles, decorOverlay, offsetX, offsetY } = this.map;
    const palette = BIOME_PALETTES[this.biome];
    // Tile kinds that sit ON TOP of a grass base instead of replacing it.
    // Path / water fully replace the ground; everything else (rocks, trees,
    // torches, banners, fountains, ice shards, castle-gate) is an obstacle
    // sprite drawn over a ground base so it can stand up in 2.5D.
    const isOverlayKind = (k: TileKind): boolean =>
      k === "rock" || k === "pine" || k === "fountain"
      || k === "torch" || k === "banner" || k === "ice_shard"
      || k === "castle-gate";

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const kind = tiles[r][c];
        const px = offsetX + c * tileSize;
        const py = offsetY + r * tileSize;

        // 1) Base ground tile (forced to depth -20).
        const baseIsGrass = kind === "grass" || isOverlayKind(kind);
        const baseKey = baseIsGrass
          ? ((c + r) % 2 === 0 ? "tile-grass" : "tile-grass-alt")
          : (TILE_TEXTURE[kind] ?? "tile-grass");
        const base = this.scene.add.image(px, py, baseKey).setOrigin(0).setDepth(-20);
        if (baseIsGrass) {
          const shades = palette.grassShades;
          if (shades && shades.length > 0) {
            base.setTint(shades[grassShadeIndex(c, r, shades.length)]);
          } else {
            base.setTint((c + r) % 2 === 0 ? palette.grassA : palette.grassB);
          }
        } else if (kind === "path") {
          base.setTint((c + r) % 2 === 0 ? palette.pathA : palette.pathB);
        } else if (palette.ambient !== 0xffffff) {
          base.setTint(palette.ambient);
        }

        // 2) Obstacle sprite on top of grass base (rocks/trees/etc.).
        // Drawn at bottom-center of tile, depth = y, casting a drop shadow.
        if (isOverlayKind(kind)) {
          const obsKey = TILE_TEXTURE[kind];
          if (obsKey && this.scene.textures.exists(obsKey)) {
            const ox = px + tileSize / 2;
            const oy = py + tileSize;

            // Grounding drop shadow
            let shadowW = 32;
            let shadowH = 10;
            if (kind === "rock") {
              shadowW = 44;
              shadowH = 12;
            } else if (kind === "fountain") {
              shadowW = 48;
              shadowH = 14;
            } else if (kind === "pine") {
              shadowW = 36;
              shadowH = 10;
            } else if (kind === "banner") {
              shadowW = 16;
              shadowH = 6;
            } else if (kind === "torch") {
              shadowW = 14;
              shadowH = 6;
            } else if (kind === "castle-gate") {
              shadowW = 54;
              shadowH = 14;
            }

            const shadow = this.scene.add.ellipse(ox, oy - 2, shadowW, shadowH, 0x000000, 0.35);
            shadow.setDepth(oy - 1);

            const obs = this.scene.add.image(ox, oy, obsKey).setOrigin(0.5, 1.0).setDepth(oy);
            if (kind === "rock") {
              obs.setTint(palette.rockColor);
            } else if (palette.ambient !== 0xffffff) {
              obs.setTint(palette.ambient);
            }
          }
        }

        // 3) Cosmetic biome overlay on grass cells (pine forest, ice shards,
        //    etc.). Kept as a separate image so it can be removed when a
        //    tower is placed on that cell.
        if (kind === "grass" && decorOverlay) {
          const ov = decorOverlay[r][c];
          if (ov && this.scene.textures.exists(ov)) {
            const ox = px + tileSize / 2;
            const oy = py + tileSize;

            // Grounding drop shadow for cosmetic overlay
            let shadowW = 32;
            let shadowH = 10;
            if (ov === "tile-pine") {
              shadowW = 36;
              shadowH = 10;
            } else if (ov === "tile-rock") {
              shadowW = 44;
              shadowH = 12;
            } else if (ov === "tile-ice-shard") {
              shadowW = 28;
              shadowH = 9;
            }

            const shadow = this.scene.add.ellipse(ox, oy - 2, shadowW, shadowH, 0x000000, 0.35)
              .setDepth(oy - 1)
              .setName(`decor-shadow-${c}-${r}`);

            const oi = this.scene.add
              .image(ox, oy, ov)
              .setOrigin(0.5, 1.0)
              .setDepth(oy)
              .setName(`decor-${c}-${r}`);
            if (palette.ambient !== 0xffffff) oi.setTint(palette.ambient);
          }
        }
      }
    }
  }

  /** Hide / remove the cosmetic overlay at a given grid cell (e.g. when a
   *  tower is placed there). No-op for image-backed maps (no overlays). */
  clearOverlayAt(c: number, r: number): void {
    if (this.hasBackgroundImage()) return;
    const name = `decor-${c}-${r}`;
    const obj = this.scene.children.getByName(name);
    if (obj) obj.destroy();

    const shadowName = `decor-shadow-${c}-${r}`;
    const shadowObj = this.scene.children.getByName(shadowName);
    if (shadowObj) shadowObj.destroy();

    if (this.map.decorOverlay) this.map.decorOverlay[r][c] = null;
  }
}
